import { httpRequest } from '../../shared/lib/utils/httpClient';
import { rowsPerPage } from '../../pages/constants';
import type { Facility } from './FacilityService';

export interface ClinicianList {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  cliniciansList?: Clinician[];
  facilityToClinicianMapsList?: Clinician[];
}
export interface FacilityList {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  clinicianToFacilityMapsList: Facility[];
}

export interface Clinician {
  id: number;
  providerBusinessId: string | null;
  firstName: string | null;
  lastName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  status: string | null;
  zoomUserKey: string | null;
  createdOn: string | null;
  modifiedOn: string | null;
  associatedFacilityCount: number;
}

interface ClinicianCategory {
  id: number;
  name: string;
}

interface ClinicianTeam {
  id: number;
  name: string;
}

export interface ClinicianMetaData {
  clinicianCategories: ClinicianCategory[];
  clinicianTeams: ClinicianTeam[];
  usStates: string[];
  titles: string[];
  genders: string[];
}

export interface BindStringArray {
  id: string;
  name: string;
}

interface UserInfo {
  id: number;
  userBusinessId: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  suffix: string;
  categoryId: number;
  status: boolean;
  email: string;
  alternateEmail: string;
  phone: string;
  alternatePhone: string;
  gender: string;
  dateOfBirth: string;
  notes: string;
  maxConcurrentConsults: number;
}

interface ProviderDetails {
  id: number;
  zoomUserId: string;
  npi: string;
}

interface Address {
  id: number;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  county: string;
}

export interface ClinicianData {
  userInfo: UserInfo;
  providerDetails: ProviderDetails;
  address: Address;
}

export interface ClinicianRequest {
  file: File | null;
  clinicianRequest: ClinicianData;
}

export interface AzureEntraUsers {
  email: string;
  firstName: string;
  lastName: string;
  userName: string;
}

export interface SaveClinicianResponse {
  success: boolean;
  clinicianId: number;
  message: string;
}

export const getClinicianList = async (
  userId: string,
  page: number = 0,
  size: number = rowsPerPage,
  sortField?: string,
  sortType?: string,
  searchTerm?: string,
  statusId?: number[]
): Promise<ClinicianList> => {
  const params: Record<string, string | number> = {
    page,
    size,
  };

  if (sortField) params.sortField = sortField;
  if (sortType) params.sortType = sortType;
  if (searchTerm) params.searchTerm = searchTerm;
  if (statusId && statusId.length > 0) params.statusId = statusId.join(',');

  const response = await httpRequest<ClinicianList>({
    method: 'GET',
    url: '/operation/clinicians',
    params,
    headers: { userId },
  });
  return response;
};

export const getClinicianSpecificToFacilityIdList = async (
  // userId: string,
  facilityId?: number,
  page: number = 0,
  size: number = rowsPerPage,
  sortField?: string,
  sortType?: string,
  searchTerm?: string,
  statusId?: string
): Promise<ClinicianList> => {
  const params: Record<string, string | number> = {
    page,
    size,
  };

  if (sortField) params.sortField = sortField;
  if (sortType) params.sortType = sortType;
  if (searchTerm) params.searchTerm = searchTerm;
  if (statusId) params.statusFilter = statusId;
  const userId = localStorage.getItem('userId') ?? '0';
  const response = await httpRequest<ClinicianList>({
    method: 'GET',
    url: `/operation/facility-to-clinician-maps?facilityId=${facilityId}`,
    params,
    headers: { userId },
  });
  return response;
};
export const getFacilitiesSpecificToClinicianIdList = async (
  clinicianId?: number,
  page: number = 0,
  size: number = rowsPerPage,
  sortField?: string,
  sortType?: string,
  searchTerm?: string,
  statusId?: string
): Promise<FacilityList> => {
  const params: Record<string, string | number> = {
    page,
    size,
  };
  const userId = localStorage.getItem('userId') ?? '0';

  if (sortField) params.sortField = sortField;
  if (sortType) params.sortType = sortType;
  if (searchTerm) params.searchTerm = searchTerm;
  if (statusId) params.statusFilter = statusId;

  const response = await httpRequest<FacilityList>({
    method: 'GET',
    url: `/operation/clinician-to-facility-maps?clinicianId=${clinicianId}`,
    params,
    headers: { userId },
  });
  return response;
};

export const getAzureEntraUsers = async (
  UserType: string
): Promise<AzureEntraUsers[]> => {
  const response = await httpRequest<AzureEntraUsers[]>({
    method: 'GET',
    url: `/operation/azure-users/unassigned?userType=${UserType}`,
  });
  return response;
};

export const getClinicianMetaData = async (
  userId: string
): Promise<ClinicianMetaData> => {
  const response = await httpRequest<ClinicianMetaData>({
    method: 'GET',
    url: `/operation/clinician/metadata`,
    headers: { userId },
  });
  return response;
};

export const saveOrUpdateClinitian = async (
  userId: string,
  action: string,
  payload: ClinicianData
) => {
  const apiAction = action === 'Save' ? 'SAVE' : 'UPDATE';
  const response = await httpRequest<SaveClinicianResponse>({
    method: 'PUT',
    url: `/operation/clinician?action=${apiAction}`,
    headers: { userId },
    data: payload,
  });
  return response;
};

export const getClinicianDetailsById = async (
  userId: string,
  clinicianId: number
): Promise<ClinicianRequest> => {
  const response = await httpRequest<ClinicianRequest>({
    method: 'GET',
    url: `/operation/clinician?clinicianId=${clinicianId}`,
    headers: { userId },
  });
  return response;
};
